import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2, Box, ExternalLink, Sparkles, MoreHorizontal, Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const WRITE_ROLES = ["owner", "admin", "researcher"];

export default function Products() {
  const { t } = useTranslation(["products", "common", "errors"]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState("__all");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        api.get(`/products?include_archived=${showArchived ? "true" : "false"}`),
        api.get(`/brands?include_archived=${showArchived ? "true" : "false"}`),
      ]);
      setProducts(pRes.data);
      setBrands(bRes.data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [t, showArchived]);

  useEffect(() => { load(); }, [load]);

  const brandsById = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);
  const filtered = useMemo(
    () => (brandFilter === "__all" ? products : products.filter((p) => p.brand_id === brandFilter)),
    [products, brandFilter]
  );

  const onArchive = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.id}/archive`);
      toast.success(t("products:detail.archived"));
      setProducts((prev) => prev.map((row) => row.id === p.id ? { ...row, archived_at: data.archived_at } : row));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };

  const onUnarchive = async (p) => {
    try {
      await api.post(`/products/${p.id}/unarchive`);
      toast.success(t("products:detail.unarchived"));
      setProducts((prev) => prev.map((row) => row.id === p.id ? { ...row, archived_at: null } : row));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };

  return (
    <div className="space-y-8" data-testid="products-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <Box className="mr-1 inline h-3 w-3" strokeWidth={2} /> EvidenceLab
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("products:list.title")}</h1>
          <p className="max-w-xl text-sm text-slate-500">{t("products:list.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger data-testid="products-brand-filter" className="h-10 w-56">
              <SelectValue placeholder={t("products:list.filterByBrand")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("products:list.filterByBrand")}</SelectItem>
              {brands.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
            <Switch
              id="products-show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
              data-testid="products-show-archived-toggle"
            />
            <Label htmlFor="products-show-archived" className="cursor-pointer text-xs text-slate-600">
              {t("products:list.showArchived")}
            </Label>
          </div>
        </div>
      </header>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="products-table-container">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common:loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="px-8 py-14 text-center" data-testid="products-empty">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
              <Box className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900">{t("products:list.empty.title")}</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{t("products:list.empty.body")}</p>
            <Button asChild className="mt-5 bg-slate-900 text-white hover:bg-slate-800">
              <Link to="/brands">{t("products:list.empty.cta")}</Link>
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-200 bg-slate-50">
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("products:list.columns.name")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("products:list.columns.brand")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("products:list.columns.category")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("products:list.columns.pricing")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("products:list.columns.updated")}</TableHead>
                <TableHead className="px-6 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500" aria-label="actions"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => {
                const archived = !!p.archived_at;
                return (
                  <TableRow
                    key={p.id}
                    data-testid={`product-row-${p.id}`}
                    className={`cursor-pointer border-slate-100 hover:bg-slate-50 ${archived ? "opacity-60" : ""}`}
                    onClick={() => navigate(`/products/${p.id}`)}
                  >
                    <TableCell className="px-6 py-4">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">{p.name}</span>
                          {archived && (
                            <Badge variant="outline" data-testid={`product-archived-badge-${p.id}`} className="border-amber-200 bg-amber-50 text-[10px] font-semibold text-amber-800">
                              <Archive className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> {t("products:list.archivedBadge")}
                            </Badge>
                          )}
                        </div>
                        {p.url && (
                          <a href={p.url} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600">
                            {p.url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                          </a>
                        )}
                        {p.autofill_source && (
                          <Badge variant="outline" className="mt-1 w-fit border-indigo-200 bg-indigo-50 text-[10px] text-indigo-700">
                            <Sparkles className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> AI
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{brandsById[p.brand_id]?.name || "—"}</TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{p.category || "—"}</TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{p.pricing ? p.pricing.slice(0, 40) : "—"}</TableCell>
                    <TableCell className="px-6 py-4 text-xs text-slate-500">{new Date(p.updated_at).toLocaleDateString()}</TableCell>
                    <TableCell className="px-2 py-4 text-right">
                      {canWrite && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`product-row-menu-${p.id}`}
                              className="h-8 px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                            >
                              <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {archived ? (
                              <DropdownMenuItem
                                data-testid={`product-menu-unarchive-${p.id}`}
                                onClick={(e) => { e.stopPropagation(); onUnarchive(p); }}
                              >
                                <ArchiveRestore className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("products:detail.unarchiveAction")}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                data-testid={`product-menu-archive-${p.id}`}
                                onClick={(e) => { e.stopPropagation(); onArchive(p); }}
                              >
                                <Archive className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("products:detail.archiveAction")}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
